/**
 * Format a date string to relative time (e.g., "2h ago", "3d ago")
 *
 * This is the canonical implementation used across the entire codebase.
 * Assumes input is UTC timestamp (ISO 8601 format) and displays in local timezone.
 *
 * @param dateString - ISO date string or date-like string
 * @returns Human-readable relative time string, or "Unknown time" if invalid
 *
 * @example
 * formatRelativeTime('2024-03-20T10:00:00Z') // "2h ago"
 * formatRelativeTime('invalid') // "Unknown time"
 * formatRelativeTime('') // "Unknown time"
 */
export function formatRelativeTime(dateString: string): string {
  // Validate input - prevent "NaN ago" from invalid dates
  if (!dateString || typeof dateString !== 'string') {
    return 'Unknown time';
  }

  const date = new Date(dateString);
  const timestamp = date.getTime();

  // Check for invalid date (NaN)
  if (isNaN(timestamp)) {
    return 'Unknown time';
  }

  const now = Date.now();
  const diffMs = now - timestamp;

  // Future timestamps (likely clock skew) — treat as just happened
  if (diffMs < 0) {
    return 'Just now';
  }

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
